/**
 * The Agentic Readiness Index in Turkish, written 2026-08-31 on the editor
 * panel's request — the tool's own words, not the page frame around it.
 *
 * A deep partial of `ari`. **Only strings.** Scoring is positional (an option is
 * worth its index plus one, 1–5) and the machine keys are untouched: `dims[].id`,
 * `dims[].side`, the `archetypes` keys, `routing` and the ladder's `n` all stay
 * exactly as English has them, because the component and the follow-up task read
 * those and not the labels.
 *
 * Written rather than translated, per the brief: business words a Turkish
 * executive already says in English stay in English — agent, agentic AI, Hub,
 * Core, Agent Core, Agent Hub, copilot, board, CFO, IT, API, SLA, P&L, ontology
 * stays "ontoloji" because that one is said in Turkish. Vendor names in the
 * systems list are names, so they are left alone.
 */
export const ariTr = {
  ui: {
    hubLabel: 'Hub',
    coreLabel: 'Core',
    hubBlurb:
      'İşin neye izin verdiği: ortada adı konmuş bir değer birimi var mı, bir agent’ın neye karar vermesine razı olurdunuz ve organizasyon bu değişimi kaldırabilir mi.',
    coreBlurb:
      'Sistemlerin gerçekte ne yapabildiği: bir agent yönetişim altındaki bilgiye erişebiliyor mu, ana sistemlerin içinde iş yapabiliyor mu ve canlıya çıktıktan sonra işletilip kanıtlanabiliyor mu.',
    indexLabel: 'Index',
    contextHeading: 'Başlamadan önce iki şey',
    contextSub:
      'İkisi de zorunlu değil ve puanınızı etkilemiyor. Değiştirdikleri şey, sonrasında konuşacaklarımızın işinize yarayıp yaramayacağı: SAP’ye uzanması gereken bir agent ile bir Excel’e uzanması gereken agent aynı problem değil.',
    sectorLabel: 'Sektör',
    sectorPlaceholder: 'Belirtmek istemiyorum',
    systemsLabel: 'Gerçekte kullandığınız ana sistemler',
    systemsSub: 'Uyanların hepsini seçebilir, hiçbirini seçmeyebilirsiniz.',
    yourArchetype: 'Arketipiniz',
    yourMove: 'Sıradaki hamleniz',
    blindHeading: 'Doğrudan eksik olanlar',
    blindSub:
      'Ölçeğin en altına koyduğunuz bileşenler. Bunlar geliştirilecek zayıflıklar değil, henüz var olmayan şeyler; ve her biri üstündeki her şeyin önünü kesiyor.',
    routedHeading: 'Biz nereden başlardık',
    routedSub: 'Arketipinize göre değil, en zayıf üç boyutunuza göre.',
    ctaHeading: 'Bunu izlenimlerinizle değil, kendi rakamlarınızla görmek ister misiniz?',
  },
  ladder: [
    {
      n: 1,
      label: 'Habersiz',
      band: 'Farkındalık',
      desc: 'Gündemde değil. Sorunun kendisi yeni.',
    },
    {
      n: 2,
      label: 'Farkında',
      band: 'Farkındalık',
      desc: 'Önemli olduğu görülüyor. Verilmiş bir karar yok.',
    },
    {
      n: 3,
      label: 'Kararlı',
      band: 'Hazırlık',
      desc: 'Karar alınmış, yazılmış, sahiplenilmiş. Kısmen yerinde.',
    },
    {
      n: 4,
      label: 'Operasyonel',
      band: 'Hazırlık',
      desc: 'Canlıda ve çalışıyor.',
    },
    {
      n: 5,
      label: 'Sistemik',
      band: 'Hazırlık',
      desc: 'Yönetişimi, ölçümü var; kurum genelinde kendiliğinden iyileşiyor.',
    },
  ],
  sectors: [
    'Bankacılık',
    'Sigorta',
    'Üretim',
    'Otomotiv',
    'Tüketici ürünleri',
    'Perakende',
    'Enerji',
    'Altyapı ve kamu hizmetleri',
    'Lojistik ve taşımacılık',
    'İlaç',
    'Sağlık',
    'Teknoloji',
    'Telekomünikasyon',
    'İnşaat',
    'Havayolları',
    'Havalimanları',
    'Turizm ve konaklama',
    'Profesyonel hizmetler',
    'Kamu',
    'Diğer',
  ],
  systems: [
    'SAP',
    'Oracle',
    'IFS',
    'Workday',
    'Salesforce',
    'Microsoft Dynamics',
    'ServiceNow',
    'Infor',
    'Epicor',
    'Sage',
    'Diğer',
    'Emin değilim',
  ],
  dims: [
    {
      id: 'value',
      side: 'hub',
      title: 'Değer ve Hedef',
      sub: 'Tanımlı bir değer birimi var mı?',
      intro:
        'Agentic AI ancak adı konmuş, ölçülen bir iş sonucuna doğrultulduğunda para kazandırır. Bu boyut, o sonucun var olup olmadığına bakıyor.',
      questions: [
        {
          component: 'Tanımlı bir değer birimi',
          text: 'Kurumunuzda agentic AI konuşulurken aslında ne konuşuluyor?',
          opts: [
            'Araçlar ve pilotlar: copilot deniyoruz, tutanı görürüz',
            'Aday kullanım senaryolarından bir liste; hiçbirine değer yazılmamış',
            'Adı konmuş iş sonuçları; ama hepsi rakama dökülmüş değil',
            'Mevcut durumu, hedefi ve iş tarafında adı konmuş bir sahibi olan sonuçlar',
            'Kanıt geldikçe önceliği yeniden belirlenen, yönetilen bir sonuç portföyü',
          ],
        },
        {
          component: 'Mevcut durum, hedef ve iş tarafı sahibi',
          text: 'En öncelikli agentic girişiminiz için bugün elinizde ne var?',
          opts: [
            'Aşağıdakilerin hiçbiri; değer diliyle hiç çerçevelenmedi',
            'Niteliksel bir fayda gerekçesi (“daha hızlı”, “daha iyi hizmet”, “verimlilik”)',
            'Rakama dökülmüş bir hedef; ama karşılaştıracak ölçülmüş bir mevcut durum yok',
            'Mevcut durum, hedef ve IT dışında sorumlu bir sahip',
            'Bunların hepsi ve CFO’nun kendi kalemi olarak tanıdığı bir P&L satırı',
          ],
        },
        {
          component: 'Kanıta dayalı önceliklendirme',
          text: 'Hangi agentic fırsatın önce geleceğine nasıl karar veriyorsunuz?',
          opts: [
            'En yüksek sesle isteyen ya da en son hangi tedarikçi demo yaptıysa',
            'Teknik olarak neyin mümkün olduğuna dair IT’nin değerlendirmesi',
            'Yapılandırılmış bir değer–efor değerlendirmesi',
            'Değer ve efor, ölçülmüş yetkinlik boşluklarıyla birlikte değerlendiriliyor',
            'Sonuçlara dair kanıt geldikçe puanlar sürekli yeniden hesaplanıyor',
          ],
        },
      ],
      next: [
        'Bir agent’ın kımıldatmasını istediğiniz tek bir sonucu iş diliyle adlandırın. “Copilot yayalım” değil, “hasar dosyası süresini %30 kısaltalım”. Sonraki her şey bu cümlenin var olmasına bağlı.',
        'Her adaya bir rakam ve bir sahip yazın. İş tarafında sahibi olmayan kullanım senaryosu bir hobidir; hobiler de bütçe dönemini atlatamaz.',
        'Hiçbir şey inşa etmeden önce mevcut durumu ölçün. Ölçmezseniz sonucun kımıldadığını hiçbir zaman kanıtlayamazsınız; bir CFO da kanıtsız iddiayı sıfıra yuvarlar.',
        'Sonuçlarınızı kuyruk gibi değil portföy gibi yönetin: her çeyrek gerçek kanıta göre yeniden sıralayın ve kımıldamayanları kapatmayı göze alın.',
        'Bu boyutun en üstündesiniz. Buradaki canlı risk portföyün dağılması; o yüzden yalnızca başlatma değil, durdurma disiplinini de koruyun.',
      ],
    },
    {
      id: 'trust',
      side: 'hub',
      title: 'Yetki ve Güven',
      sub: 'İş tarafı, makineden ne istemeye yetkili?',
      intro:
        'Yetki sınırı, deterministik kodun bittiği ve modelin muhakemesine bırakılanın başladığı çizgidir. Bu çizgiyi birinin çizmesi gerekir; ve o kişi gece yarısı tek başına bir mühendis olmamalı.',
      questions: [
        {
          component: 'Yetki sınırı',
          text: 'Agentic sistemler deterministik kodla model muhakemesini birlikte kullanır. İkisi arasındaki çizgiyi kim çizdi?',
          opts: [
            'Bu ayrımı hiç konuşmadık',
            'Mühendisler duruma göre karar veriyor; hiçbir yerde yazılı değil',
            'Yazılı bir ilke var ve yeni geliştirmelerde uygulanıyor',
            'Sınır her kullanım senaryosu için ayrı çiziliyor ve iş sahibi onaylıyor',
            'Platformda uygulanan bir politika; modeller ya da riskler değiştikçe gözden geçiriliyor',
          ],
        },
        {
          component: 'İnsanın yetkisi ve karar hakları',
          text: 'Bir agent yanlış çıkan bir aksiyon aldı ve bu gerçek paraya mal oldu. Ne olur?',
          opts: [
            'Bilmiyoruz; başımıza gelmedi',
            'Biri gayriresmî olarak inceler; sorumluluğun kimde olduğu belirsiz',
            'Adı konmuş bir insan sorumludur ve olay yönetimi yazılıdır',
            'O aksiyon sınıfı için onay noktaları ve eskalasyon yolu önceden tanımlanmıştır',
            'Karar hakları aksiyon bazında resmen devredilmiştir, onay noktaları çalışma zamanında zorunludur ve her aksiyon geriye dönük yeniden kurulabilir',
          ],
        },
        {
          component: 'Agent kimliği ve denetim izi',
          text: 'Agent’larınız sistemlere nasıl giriş yapıyor ve arkalarında nasıl bir iz bırakıyor?',
          opts: [
            'Bunu hiç düşünmedik',
            'Ortak ya da ödünç bir insan hesabı kullanıyorlar; log da her uygulamanın verdiği kadar',
            'Agent’ların kendi kimlik bilgileri var; aksiyonlar sistem sistem loglanıyor',
            'Agent’ların yetki kapsamı tanımlı, birinci sınıf bir kimliği ve tek bir denetim izi var',
            'Kimlik, yetki ve denetim merkezî olarak yönetiliyor; denetçi ya da düzenleyici istediğinde kanıt dosyası üretilebiliyor',
          ],
        },
      ],
      next: [
        'Yetki sınırını bir üst yönetim gündemine taşıyın. “Bir modelin neye karar vermesine razıyız?” bir iş sorusudur; geç cevaplanırsa kurum kendi açığını bir olayla öğrenir.',
        'Sınırı tek sayfada yazın: ne deterministik kod kalıyor, ne modele bırakılıyor ve neden. Yazılmamış ilkeler ekip değişince kaybolur.',
        'Genel ilkeden kullanım senaryosu bazında onaya geçin; onay noktaları ve eskalasyon yolu canlıya çıkıştan önce, ilk kötü aksiyondan sonra değil.',
        'Sınırı dokümanda değil platformda zorunlu kılın, agent’lara birinci sınıf kimlik verin ve her aksiyonu, o odada olmayan biri için yeniden kurulabilir hale getirin.',
        'Bu boyutun en üstündesiniz. Modeller değiştikçe sınırı yeniden test edin; artan kabiliyet, çoktan verdiğiniz yetkiyi siz fark etmeden genişletir.',
      ],
    },
    {
      id: 'change',
      side: 'hub',
      title: 'Değişim ve Yetkinlik',
      sub: 'İnsanlar gerçekten bu şekilde çalışacak mı?',
      intro:
        'Her agentic program eninde sonunda organizasyonun kendisine çarpar. Sahiplik, yeniden tasarlanmış iş ve çalışanın güveni; teknolojinin benimseneceğini mi yoksa sessizce etrafından dolaşılacağını mı belirler.',
      questions: [
        {
          component: 'Üst yönetim sahipliği ve dönüşüm yönetişimi',
          text: 'Agentic AI gündeminizin sahibi kim?',
          opts: [
            'Özellikle kimse; işler köşelerde yürüyor',
            'Bir teknoloji girişimi olarak IT ya da veri/AI ekibi',
            'Bütçesi olan, adı konmuş bir üst yönetici sponsor',
            'Tüm portföy üzerinde yetkisi olan, fonksiyonlar arası bir dönüşüm ofisi',
            'Board seviyesinde sahiplik ve yalnızca teslimatı değil faydayı yöneten bir dönüşüm ofisi',
          ],
        },
        {
          component: 'İşin ve rollerin yeniden tasarımı',
          text: 'Bir agent sürecin bir parçasını devraldığında sürece ve çevresindeki rollere ne oluyor?',
          opts: [
            'Böyle bir soru henüz çıkmadı',
            'Agent mevcut sürecin üzerine cıvatalanıyor; başka hiçbir şey değişmiyor',
            'Sürtünme nerede görünürse süreci orada, yerel olarak düzeltiyoruz',
            'Roller, devir noktaları ve kontroller canlıya çıkmadan önce agent etrafında yeniden tasarlanıyor',
            'İş tasarımı kalıcı bir disiplin: organizasyon, süreç ve agent’lar birlikte tasarlanıyor',
          ],
        },
        {
          component: 'Çalışanın güveni ve hazırlığı',
          text: 'Çalışanlarınız bugün agentic AI’ı nasıl anlatırdı?',
          opts: [
            'Çoğu insanın teması olmadı ve biz de hiç sormadık',
            'Endişe ve söylenti; iletişim zayıf kaldı',
            'Temkinli bir ilgi; iletişim yapıyoruz, isteyene eğitim veriyoruz',
            'Geniş katılım, role özel hazırlık; itirazlar aktif olarak toplanıp cevaplanıyor',
            'İnsanlar agent’ları kendileri öneriyor ve tasarlıyor; teşvikler bunu ödüllendiriyor, benimseme ölçülüyor',
          ],
        },
      ],
      next: [
        'Sorumlu tek bir üst yönetici belirleyin. Sahibi olmayan agentic AI, sahibi rol değiştirdiğinde tek tek ölen deneyler topluluğu olarak kalır.',
        'Sahipliği IT’den çıkarın. Bu bir teknoloji girişimi olarak durduğu sürece işin geri kalanı sonucu isteğe bağlı sayar; haksız da sayılmazlar.',
        'Sponsora, portföy üzerinde yetkisi olan fonksiyonlar arası bir dönüşüm ofisi verin ve değişim yönetimini bu ofisin yanına değil içine koyun.',
        'Ofisi teslimatı yönetmekten faydayı yönetmeye geçirin. İşi, projelerin zamanında kapandığını değil değerin gerçekten oluştuğunu kanıtlamaktır.',
        'Bu boyutun en üstündesiniz. Bundan sonraki kısıt çalışanın güveni; o yüzden yalnızca teslimatı değil, algıyı ve benimsemeyi de ölçmeye devam edin.',
      ],
    },
    {
      id: 'knowledge',
      side: 'core',
      title: 'Bilgi ve Veri',
      sub: 'Bir agent bilmesi gerekeni bilebilir mi?',
      intro:
        'Agent’lar model kalitesinde tökezlemeden çok önce belirsizlikte tökezler. Veriye erişim, ortak tek bir sözlük ve bilinçli bir hafıza tasarımı; bir demoyu bir iş arkadaşından ayıran şeydir.',
      questions: [
        {
          component: 'Yönetişimi olan bir veri temeli',
          text: 'Bir agent’ın, ana bir iş kararının arkasındaki veriye ihtiyacı olsa, ona nasıl ulaşırdı?',
          opts: [
            'Bir insana sorması gerekirdi; o da Excel çıkarırdı',
            'Veri kaynak sistemlerde duruyor; biri özel bir entegrasyon yazardı',
            'Yönetişimi olan bir veri platformu var ve ana alanları kapsıyor',
            'Katalog, veri soyağacı ve kalite SLA’ları olan bir platform; agent’lara kapsamı tanımlı erişim verilebiliyor',
            'Bunların hepsi ve makinenin tüketmesi için tasarlanmış, tazelik garantisi olan veri ürünleri',
          ],
        },
        {
          component: 'Anlam ve ontoloji: ortak sözleşme',
          text: 'Üç ayrı departmana “aktif müşteri” ya da “zamanında teslimat” nedir diye sorsanız kaç cevap alırdınız?',
          opts: [
            'Üç; ve bunları uyumlandırmayı kimse denemedi',
            'Üç; ve bunun bir sorun olduğunu biliyoruz',
            'Sözlükte tek bir tanım var, ama sistemlerin hepsi ona uymuyor',
            'Bir semantik katman varlıkları, metrikleri ve aksiyonları tanımlıyor; uygulamalar oradan okuyor',
            'Ontoloji sözleşmenin kendisi: agent’lar, BI ve uygulamalar oraya bakıyor ve ontolojideki değişiklikler yönetiliyor',
          ],
        },
        {
          component: 'Bir tasarım kararı olarak agent hafızası',
          text: 'Bir agent, bir görevden diğerine ne hatırlıyor?',
          opts: [
            'Bunu hiç düşünmedik',
            'Hiçbir şey; her çalışma prompt’ta ne varsa onunla sıfırdan başlıyor',
            'Tek bir oturum içinde konuşma geçmişi',
            'Neyin kalıcı olacağını bilerek tasarlıyoruz: ne öğreniyor, neyi hatırlıyor, neyi unutmak zorunda',
            'Hafıza; çalışma, olay, prosedür ve anlam katmanları olarak mimari kurgulanmış, saklama ve gizlilik açısından yönetiliyor ve sonuçlarla birlikte iyileşiyor',
          ],
        },
      ],
      next: [
        'Bir sonraki mimari toplantınızda soruyu yüksek sesle sorun: bir agent bu veriye insan devreye girmeden ulaşabilir miydi? Cevap genelde hayırdır ve asıl bulgu budur.',
        'Öncelikli sonuçlarınızın gerçekten dayandığı iki üç veri alanını seçip önce onları yönetişime alın. Kurum çapındaki veri programları, agent yol haritanızın onlara ihtiyaç duyduğu tarihten çok sonra biter.',
        'Katalog, veri soyağacı ve kalite SLA’ları ekleyin; sonra varlıklar, metrikler ve aksiyonlar için tek bir ortak sözlük tanımlayın. Canlıda agent’ları en sık düşüren tek şey belirsizliktir.',
        'Veri ürünlerini makinenin tüketmesi için tasarlayın, ontolojiyi her şeyin baktığı sözleşme haline getirin ve ardından agent’larınızın ne hatırlayacağına bilinçli bir karar verin.',
        'Bu boyutun en üstündesiniz. Ontoloji değişikliğini bir API değişikliği gibi yönetin; sessiz bir yeniden tanımlama, altındaki bütün agent’ları bozar.',
      ],
    },
    {
      id: 'systems',
      side: 'core',
      title: 'Sistemler ve Aksiyon',
      sub: 'Gerçekten bir şey yapabiliyor mu?',
      intro:
        'Yalnızca okuyabilen bir agent, arama motorudur. Kayıt defteri, aracı katmanı ve kapsamı tanımlı yetkileriyle entegrasyon ve aksiyon geçidi; agentic AI’ın hem faydalı hem de tehlikeli hale geldiği yerdir.',
      questions: [
        {
          component: 'Ana sistemlere programatik erişim',
          text: 'SAP, Oracle, IFS, Workday, Salesforce ya da muadili olsun; ana kurumsal sistemlerinize programatik olarak ne kadar ulaşılabiliyor?',
          opts: [
            'Belirsiz; tedarikçiye ya da sistem entegratörümüze sormamız gerekir',
            'Ağır özelleştirilmiş, eski sürümler; erişim çoğunlukla ekran üzerinden ya da toplu dosyayla',
            'Ana sistemlerde modern API’ler var; erişim proje bazında veriliyor',
            'Ana sistemlerin üstünde sahibi belli, kararlı bir API ve olay katmanı',
            'Ana sistemler, agent’ın tüketmesi için tasarlanmış, yönetişimi ve sürümü olan servisler olarak açılmış',
          ],
        },
        {
          component: 'Yönetişimi olan bir araç yüzeyi',
          text: 'Yeni bir agent, bir araca ya da sisteme nasıl erişim alıyor?',
          opts: [
            'Böyle bir mekanizma yok; canlıda bir şey de yok',
            'Bir geliştirici noktadan noktaya entegrasyon yazıyor',
            'Yeniden kullanılabilir bağlayıcılar var, ama erişim duruma göre veriliyor',
            'Kapsamı tanımlı yetkiler, standart protokoller ve bir onay akışıyla çalışan bir araç kayıt defteri ve aracı katmanı',
            'Geçit, zorunlu güvenlik sınırının kendisi: her agent aksiyonu oradan geçiyor ve politika yetki bazında uygulanıyor',
          ],
        },
        {
          component: 'Aksiyon güvenliği ve yazma yetkisi',
          text: 'Agent’larınızdan hangileri yalnızca okuyup öneri vermek yerine bir şeyi değiştirebiliyor?',
          opts: [
            'Hiçbiri; neye izin vereceğimizi de konuşmadık',
            'Hiçbiri; her şey salt okunur ya da insanın kopyala–yapıştırı — karar olduğu için değil, öyle geldiği için',
            'Birkaçı, düşük riskli alanlarda, insan onayının arkasında',
            'Yazma yetkisi aksiyon sınıfına göre veriliyor; izole ortamda test ediliyor ve geri alınabiliyor',
            'Yazma yetkisi sıradan ve güvenli: sınırlı, geri alınabilir, izleniyor ve kanıt geldikçe genişletiliyor',
          ],
        },
      ],
      next: [
        'Önce gerçekte nerede durduğunuzu öğrenin. En önemli üç sisteminizi alın; hangisine programatik olarak, hangi sürümle ve ne kadar özelleştirme borcuyla ulaşılabildiğini tespit edin.',
        'Noktadan noktaya entegrasyon yazmayı bırakın. Her özel entegrasyon, geri alamayacağınız bir yetki ve hiçbir şemada görünmeyen bir bağımlılıktır.',
        'Sistemlerin önüne bir kayıt defteri ve aracı katman koyun: kapsamı tanımlı yetkiler, standart protokoller, tek bir onay akışı. Gerçek güvenlik sınırınız model değil, burasıdır.',
        'Okumadan yazmaya adım adım geçin: aksiyon sınıfına göre yetki verin, izole ortamda deneyin, geri alınabilir yapın ve hevese göre değil kanıta göre genişletin.',
        'Bu boyutun en üstündesiniz. Geçit ancak hiçbir şey onu atlamıyorsa koruyor; bu yüzden yan yolları düzenli aralıklarla denetleyin.',
      ],
    },
    {
      id: 'operate',
      side: 'core',
      title: 'İşletme ve Kanıt',
      sub: 'Çalıştırabiliyor, güvenebiliyor, karşılayabiliyor ve ölçekleyebiliyor musunuz?',
      intro:
        'Agent’lar sessizce bozulur. Değerlendirme, gözlemlenebilirlik ve sonuç başına maliyet bilinmeden; çalışan bir sistemle dört ay önce çalışmayı bırakmış bir sistemi ayırt edemezsiniz.',
      questions: [
        {
          component: 'Canlı çalışma ortamı ve güvenilirlik',
          text: 'Bugün canlıda, gerçek kullanıcıların bağımlı olduğu hangi agentic iş çalışıyor?',
          opts: [
            'Henüz yok; okuyor ve konuşuyoruz',
            'Gerçek hacimle karşılaşınca ayakta kalmayan demolar ve pilotlar',
            'Canlıda bir iki şey var; desteği gayriresmî yürüyor',
            'Canlıda birkaçı; SLA’ları, izlemesi ve nöbetçi bir sahibi var',
            'Agent’lar desteklenen bir kurumsal yetkinlik: sürüm, versiyon ve geri alma disiplini var',
          ],
        },
        {
          component: 'Değerlendirme ve gözlemlenebilirlik',
          text: 'Bir agent lansmanda iyi çalıştı. Kötüleştiğini nasıl anlardınız?',
          opts: [
            'Biri şikâyet edince öğrenirdik',
            'Onu kuran ekibin ara sıra yaptığı kontrollerle',
            'Tanımlı kalite metrikleri var, düzenli olarak gözden geçiriliyor',
            'Her değişiklikte otomatik değerlendirme; regresyon setleri ve sapma uyarılarıyla',
            'Değerlendirme sürekli çalışıyor ve bulguları hafızaya, anlam katmanına ve politikaya geri besleniyor',
          ],
        },
        {
          component: 'Birim ekonomisi: sonuç başına maliyet',
          text: 'Bir birim agentic çıktı size kaça mal oluyor?',
          opts: [
            'Bilmiyoruz ve hiç sormadık',
            'Toplam bir AI faturası görüyoruz ama hiçbir şeye dağıtamıyoruz',
            'Maliyet uygulama ya da ekip bazında takip ediliyor',
            'Sonuç başına maliyet biliniyor ve o sonucun getirdiği değerle karşılaştırılıyor',
            'Birim ekonomisi yönlendirme, önbellek ve toplu işleme ile aktif olarak tasarlanıyor ve neyin ölçekleneceğine karar vermekte kullanılıyor',
          ],
        },
      ],
      next: [
        'Gerçek bir kullanıcının bağımlı olduğu tek bir şeyi canlıya alın. Pilotlar neyin bozulacağı hakkında neredeyse hiçbir şey öğretmez, çünkü bozulduklarında kaybedecek bir şey yoktur.',
        'Her agent için “iyi”nin ne demek olduğuna ve kötüleştiğini nasıl fark edeceğinize daha lansmandan önce karar verin. Agent’lar sessizce ve kibarca başarısız olur.',
        'Her değişiklikte değerlendirmeyi otomatikleştirin ve maliyeti dağıtmaya başlayın. Birim maliyetini bilmediğiniz bir agent sorumlulukla değil, ancak umutla ölçeklenir.',
        'Döngüyü kapatın: sonuç kanıtı hafızaya, anlam katmanına ve politikaya geri beslensin; birim ekonomisini de yönlendirme, önbellek ve toplu işlemeyle bilinçli olarak tasarlayın.',
        'Bu boyutun en üstündesiniz. Avantajınız artık o geri besleme döngüsünün hızı; onu süreçten koruyun.',
      ],
    },
  ],
  archetypes: {
    ambition: {
      name: 'Hedef Önde',
      short: 'Core’unuzun güvenle taşıyabileceğinden fazlasına izin veriyorsunuz.',
      long: 'İşin zor yarısını düşünmüşsünüz: niyet var, sahiplik var ve değeri tek cümleyle anlatabilecek biri var. Eksik olan altındaki makine: bir agent’ın gerçekten erişebileceği veri, üzerinden aksiyon alacağı yönetişimli yüzey ve gelecek çeyrekte hâlâ çalışıp çalışmadığını size söyleyecek kanıt döngüsü. En sık görülen pozisyon bu; ve agentic pilotların kavram ispatıyla canlı arasında takılmasının da tam sebebi bu. Kısıt hedef değil, model de değil. Kısıt Agent Core.',
      move: 'Yeni kullanım senaryosu eklemeyin. Çoktan adını koyduğunuz iki üç sonucun üzerine Core’u bir kez kurun.',
    },
    capability: {
      name: 'Altyapı Önde',
      short: 'İş tarafının nasıl kullanacağına karar verdiğinden fazlasını kurmuşsunuz.',
      long: 'Mühendislik tarafınız üst yönetiminizin önünde. Platform, veri ve kimi durumda çalışan agent’lar zaten var; ama iş tarafı neyi devretmek istediğini, agent yanıldığında kimin sorumlu olduğunu ya da tüm bunların hangi sonucu kımıldatması gerektiğini tanımlamamış. Değer platformun içinde mahsur kalmış. Bu pozisyon tersine göre daha seyrek ve daha can sıkıcı, çünkü yatırım çoktan yapılmış. İyi haber: eksik olan, halihazırda kurduğunuzun yanında çok daha ucuz.',
      move: 'Bir şey daha inşa etmeden önce üst yönetimle bir yetki ve değer çalıştayı yapın.',
    },
    observer: {
      name: 'İzleyici',
      short: 'Agentic AI kurumunuzda henüz bir yetkinlik değil, bir sohbet konusu.',
      long: 'Modelin iki tarafı da yerinde değil; bu, kamuoyundaki konuşmanın ima ettiğinden hem daha dürüst hem de daha yaygın bir pozisyon. Avantajınız, yanlış sıraya henüz para harcamamış olmanız: faaliyet olarak sizin önünüzde görünen birçok kurum hazırlık olarak önünüzde değil, çünkü temeli kurmadan pilot kurdular. Elinizdeki en ucuz hamle, herhangi bir teknoloji kararı alınmadan önce gerçekte hangi iş değerinin peşinde olduğunuza karar vermek.',
      move: 'Araçtan değil sonuçtan başlayın. Adı konmuş, ölçülen, sahibi olan tek bir iş sonucu.',
    },
    compounding: {
      name: 'Birlikte Büyüyen',
      short: 'İki taraf birlikte ilerliyor. Kısıtınız hazırlık değil, hız.',
      long: 'Yetki ve kabiliyet aynı adımda ilerliyor; bu nadirdir ve agentic yatırımın gerçekten bileşik getiri ürettiği koşuldur: teslim edilen her sonuç bir sonrakini ucuzlatır, çünkü Core ortaktır ve yönetişim zaten vardır. Bu noktada risk profiliniz değişir. Tehdit artık teknik hazırlık değil; portföyün dağılması, ontolojinin kayması ve kimsenin yeniden gözden geçirmediği hızda genişleyen bir yetki yüzeyidir.',
      move: 'Portföyü yönetin ve yetki sınırını belirli bir düzenle yeniden test edin.',
    },
  },
  solutions: {
    strategy: {
      nm: 'Strateji Danışmanlığı: DAIMI / AIMI',
      ds: 'Olgunluk değerlendirmesinin, önceliklendirilmiş ve rakama dökülmüş bir dönüşüm yol haritasına dönüşmüş hali. Discovery Platform ile.',
    },
    tmo: {
      nm: 'Dönüşüm Yönetimi',
      ds: 'Yalnızca teslimatı değil faydayı yöneten; değişim yönetimini ve kültürü merkezine alan bir dönüşüm ofisi. Navigator Platform ile.',
    },
    enterprise: {
      nm: 'Kurumsal Yapay Zeka Çözümleri',
      ds: 'Kurumsal sınıf agentic altyapı olarak Agent Core ve tasarım ile çalıştırma ortamı olarak Agent Hub; SAP, Oracle, IFS, Workday ve Salesforce ortamları için kurgulandı.',
    },
  },
}
